import { useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import _ from "lodash";
import { useNavigate } from "react-router-dom";

import { setCurrentNavigation } from "../../slices/navigationSlice";
import useMount from '../../hooks/useMount';
import LoadingComponent from "../Commons/LoadingComponent";
import AlertModal from "../Commons/AlertModal";
import ConfirmModal from "../Commons/ConfirmModal";
import BackButton from "../Commons/BackButton";
import userApi from "../../api/userApi";
import unitApi from "../../api/unitApi";
import { getLanguages } from "../../slices/languageSlice";
import { getAuth, setAuth } from "../../slices/authSlice";
import { getLabel, getLanguage, setLanguage } from "../../tools/Dictionary";
import useLog from "../../hooks/useLog";

import "./Setting.css";

const SettingPage = (props) =>
{
  //Logs
  const { logPageView, logUserAction } = useLog();

  //Navigation
  const Navigate = useNavigate();

  //Props
  const IsDesktop = props.IsDesktop;

  //Redux
  const Dispatch = useDispatch();
  const Language = useSelector(getLanguages).filter(lang => lang.code != "ES"); // Exclude Spanish language;
  const Auth = useSelector(getAuth); 

  //state
  const [form, setForm] = useState({
    languageCode: ""
  });
  //const [error, setError] = useState({});
  const [user, setUser] = useState(null);
  const [temperatureUnit, setTemperatureUnit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    Title: "",
    Message: "",
    Confirm: "",
    onConfirm: () => void 0,
    Display: false
  });
  const [confirm] = useState({
    Title: "",
    Message: "",
    Cancel: "",
    onCancel: () => void 0,
    Confirm: "",
    onConfirm: () => void 0,
    Display: false
  });


  useMount(() =>
  {

    logPageView("Setting");
    loadData();

    Dispatch(setCurrentNavigation("Setting"));
  }, []);


  const loadData = async (updateAuth = false) =>
  {

    try
    {

      setLoading(true);
      
      let [userData, unitData] = await Promise.all([
        userApi.getCurrentUser(),
        unitApi.getUnits({ filter: "group eq 'Temperature'", select: `_id,label`, translate: getLanguage().code }),
      ]);

      if (userData.Success)
      {


        if (updateAuth){

          let toUpdateAuth = _.cloneDeep(Auth);

          toUpdateAuth.User.languageCode = userData.Data.languageCode;
          toUpdateAuth.User.temperatureUnit = userData.Data.temperatureUnit;

          Dispatch(setAuth(toUpdateAuth.User));

        }

        

        setUser(userData.Data);
        setForm({
          languageCode: userData.Data.languageCode,
          temperatureUnit: userData.Data.temperatureUnit ? userData.Data.temperatureUnit._id : undefined
        });
      } else
        throw new Error(userData.Error.label);

      if (unitData.Success)
      {
        setTemperatureUnit(unitData.Data);
      } else
        throw new Error(unitData.Error.label);


      setLoading(false);

    } catch (err)
    {

      setLoading(false);

      setAlert({
        Title: getLabel("App", "Error"),
        Message: err.message || getLabel("App", "Sorry, an unknown error has occurred. Please try again later or contact our support team for assistance."),
        Confirm: getLabel("App", "Ok"),
        onConfirm: () => setAlert(alert => ({ ...alert, Display: false })),
        Display: true
      });

    }

  };

  const onChangeLanguage = (e) => {

    let languageCode = e.target.value;

    setForm(form => ({
      ...form,
      languageCode
    }));

  };

  const onChangeTemperatureUnit = (e) =>
  {

    let temperatureUnit = e.target.value;

    setForm(form => ({
      ...form,
      temperatureUnit
    }));

  };

  const onClickSave = async() => {

    try
    {

      setLoading(true);

      let updatedUser = await userApi.updateCurrentUser(form);


      if (!updatedUser.Success)
        throw new Error(updatedUser.Error.label);

      if (user.languageCode != form.languageCode){
        
        setLanguage(form.languageCode, "");
      }

      let settingChange = {
        languageCodeChange: form.languageCode != user.languageCode,
        temperatureUnitChange: form.temperatureUnit != user.temperatureUnit?._id
      };

      if (settingChange.languageCodeChange){
        settingChange.prevLanguageCode = user.languageCode;
        settingChange.newLanguageCode = form.languageCode;
      }

      if (settingChange.temperatureUnitChange){
        settingChange.prevTemperatureUnit = temperatureUnit.find(t => t._id === user.temperatureUnit?._id);
        settingChange.newTemperatureUnit = temperatureUnit.find(t => t._id === form.temperatureUnit);
      }

      logUserAction("Settings updated", settingChange);

      loadData(true);



    } catch (err)
    {

      setLoading(false);

      setAlert({
        Title: getLabel("App", "Error"),
        Message: err.message || getLabel("App", "Sorry, an unknown error has occurred. Please try again later or contact our support team for assistance."),
        Confirm: getLabel("App", "Ok"),
        onConfirm: () => setAlert(alert => ({ ...alert, Display: false })),
        Display: true
      });

    }

  };


  return (
    <div className="page_cont">
      <LoadingComponent
        display={loading}
        label={getLabel("App", "Loading")}
        isCover={true}
      />
      <AlertModal
        Title={alert.Title}
        Message={alert.Message}
        Confirm={alert.Confirm}
        onConfirm={alert.onConfirm}
        Display={alert.Display}
      />
      <ConfirmModal
        Display={confirm.Display}
        Title={confirm.Title}
        Message={confirm.Message}
        Cancel={confirm.Cancel}
        onCancel={confirm.onCancel}
        Confirm={confirm.Confirm}
        onConfirm={confirm.onConfirm}
      />
      <BackButton
        Hide={IsDesktop}
      />
        <div className="page_title" style={{marginBottom: "20px"}}>
          {getLabel("Setting", "Settings")}
        </div>
        <div className="page_body_cont">
          <div className="setting_row" style={{fontWeight: "bold"}}>
            {getLabel("App", "Language")}
          </div>
          <div className="setting_row">
            <select
            value={form.languageCode || ""}
              className={"page_select_input"}
              onChange={onChangeLanguage}
              style={{ width: "300px", textAlign: "center", color: "black" }}
              disabled={loading}
            >
              {Language.map((l, index) => (
                <option key={index} value={l.code} style={{ textAlign: "center", color: "black" }}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
          <div className="setting_row" style={{ fontWeight: "bold" }}>
          {getLabel("Setting", "Temperature Unit")}
          </div>
          <div className="setting_row">
            <select
              value={form.temperatureUnit || ""}
              className={"page_select_input"}
              onChange={onChangeTemperatureUnit}
              style={{ width: "300px", textAlign: "center", color: "black" }}
              disabled={loading}
            >
              {temperatureUnit.map((t, index) => (
                <option key={index} value={t._id} style={{ textAlign: "center", color: "black" }}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="setting_row" style={{marginTop: "50px"}}>
            <input
              type="button"
              className="page_button page_modal_footer_button"
              value={getLabel("App", "Save")}
              style={{ maxWidth: "200px" }}
              onClick={onClickSave}
              disabled={loading}
            />  
          </div>
          <div className="setting_row" style={{marginTop: "200px"}}>
            <input
              type="button"
              className="page_button page_modal_footer_button"
              value={getLabel("Setting", "Delete account")}
              style={{ maxWidth: "200px", backgroundColor: "#b30000" }}
              onClick={() => Navigate(`/delete`)}
              disabled={loading}
            />  
          </div>
        </div>
    </div>
  )

};

export default SettingPage;